import React from 'react';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import ChartCard from '@ui/chart/ChartCard';
import DataTable, { DataTableColumn, DataTableRow } from '@ui/chart/DataTable';
import { formatMonthRange, MonthRange } from '@ui/selector/MonthRangePicker';
import type { ValueFormat } from '@ui/chart/chartFormat';
import type { RevenueScope, SegmentationReport, SegmentationView, VatBasis } from '@app/model/bi/Bi';
import { formatDate, revenueContent, revenueName } from '../Bi.utilities';
import { cellOf, CompareItem, CompareMeasure, compareRows, compareValue, firstDayOf, lastDayOf, rowOf } from './Compare.utilities';
import type { ItemData } from './CompareQueries';

type Props = {
	items: CompareItem[],
	reports: ItemData<SegmentationReport>[],
	// The keys of the Segment Groups in the order of the company.
	groupOrder: string[],
	period: MonthRange,
	view: SegmentationView,
	scope: RevenueScope,
	vat: VatBasis,
	asOf: string,
	measure: CompareMeasure,
	onMeasure: (measure: CompareMeasure) => void,
	share: boolean,
	onShare: (share: boolean) => void,
	// The row that the chart shows; a click on the same row shows the Total again.
	selectedRow: string | null,
	onSelectRow: (row: { key: string, title: string } | null) => void,
};

const MEASURES: { value: CompareMeasure, label: string }[] = [
	{ value: 'rn', label: 'Room nights' }, { value: 'rev', label: 'Revenue' }, { value: 'adr', label: 'ADR' }, { value: 'pu7', label: 'PU 7' },
];

// The table of a Property comparison (D9, D10, D11): one row for each Segment Group (or PMS segment), one column for each
// item, one measure at a time. Each column has the figures of the Segmentation report of that item alone (T4); the items
// can share properties, so there is no total over the items (T9). Share %: the part of the total of the column, for Room
// nights and Revenue only (D5). A column that loads shows "…", a column that failed "–" (T11).
const CompareTableCard: React.FunctionComponent<Props> = ({
	items, reports, groupOrder, period, view, scope, vat, asOf, measure, onMeasure, share, onShare, selectedRow, onSelectRow,
}) => {
	const shareOk = measure === 'rn' || measure === 'rev';
	const asShare = share && shareOk;
	const format: ValueFormat = asShare ? 'percent' : (measure === 'rn' || measure === 'pu7' ? 'number' : 'money');
	const columns: DataTableColumn[] = [
		{ id: 'name', label: view === 'GROUPS' ? 'Group' : 'PMS segment', sortable: false },
		...items.map(item => ({ id: item.id, label: item.label, format, decimals: asShare ? 1 : 0, role: 'series' as const, sortable: false,
			signed: measure === 'pu7', tone: measure === 'pu7' ? 'change' as const : undefined })),
	];
	const data = reports.map(r => r.data);
	// ROO2-315 part c1: a column of a property with report rules divides its ADR by the occupied rooms of its rules.
	const rulesWords = data.some(r => r !== undefined && (r.reportRules !== null || r.rulesDiffer))
		? ' ADR = Revenue ÷ the occupied rooms of each property’s report rules (Settings › Integrations › Reservations).' : '';
	const rows: DataTableRow[] = compareRows(data, groupOrder).map(row => ({
		id: row.key, muted: row.muted,
		cells: Object.fromEntries([['name', row.title], ...items.map((item, i) => {
			const r = reports[i];
			const report = r?.data;
			return [item.id, cellOf(r?.state ?? 'loading', report === undefined ? null : compareValue(report, rowOf(report, row.key), measure, asShare, scope, vat))];
		})]),
	}));
	const footer: DataTableRow = {
		id: 'total',
		cells: Object.fromEntries([['name', 'Total'], ...items.map((item, i) => {
			const r = reports[i];
			const report = r?.data;
			return [item.id, cellOf(r?.state ?? 'loading', report === undefined ? null : compareValue(report, report.total, measure, asShare, scope, vat))];
		})]),
	};
	const what = MEASURES.find(m => m.value === measure)?.label ?? '';
	const allLoading = reports.every(r => r.state === 'loading');
	const allFailed = reports.length > 0 && reports.every(r => r.state === 'error');
	const empty = !allLoading && rows.length === 0;
	const titles = new Map(rows.map(r => [r.id, String(r.cells.name)]));

	return <ChartCard title={`${view === 'GROUPS' ? 'Groups' : 'PMS segments'} · ${formatMonthRange(period)}`}
		period={`${asShare ? `${what} as a share of the total of each column` : what} · stay dates ${formatDate(firstDayOf(period.from))} – ${formatDate(lastDayOf(period.to))} · past nights actual, later nights on the books`}
		state={allFailed ? 'error' : (allLoading ? 'loading' : (empty ? 'empty' : 'default'))} onRetry={() => reports.forEach(r => r.refetch())}
		emptyLabel="No sold room nights in these stay months." errorLabel="The comparison did not load."
		actions={<div className="grid w-full grid-cols-2 gap-x-2 gap-y-4 pt-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:gap-x-4">
			<CaptionGroup caption="Measure" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
				<div className="w-full min-w-0"><ButtonTabs label="Measure of the table" value={measure} onChange={value => onMeasure(value as CompareMeasure)} options={MEASURES} /></div>
			</CaptionGroup>
			<CaptionGroup caption="Show as" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
				<div className="w-full min-w-0"><ButtonTabs label="Show the table as values or shares" value={asShare ? 'share' : 'values'} onChange={value => onShare(value === 'share')}
					options={[{ value: 'values', label: 'Values' }, { value: 'share', label: 'Share %', disabled: !shareOk, note: shareOk ? undefined : 'Room nights and Revenue only' }]} /></div>
			</CaptionGroup>
		</div>}
		caption={`One column for each property or group, with the figures of its own Segmentation report. RN = sold room nights; Revenue = ${revenueName(scope, vat)} (${revenueContent(scope)}); PU 7 = today − situation D-7 (${formatDate(asOf)}). A click on a row shows that group in the chart. Two columns can hold the same property, so the table has no total over the columns.${rulesWords}`}>
		<DataTable columns={columns} rows={rows} footer={footer} ariaLabel={`Property comparison, ${formatMonthRange(period)}`} framed={false} stickyHeader wrapHeaders
			selectedRowId={selectedRow ?? undefined}
			onRowSelect={row => onSelectRow(row.id === selectedRow ? null : { key: row.id, title: titles.get(row.id) ?? row.id })} />
	</ChartCard>;
};

export default CompareTableCard;
